import React from 'react';
import {
  getFrameMechanicConfig,
} from '../../config/campaignFrameConfig';
import { getPatternComponent } from '../../config/campaignFramePatternRegistry';

/**
 * Fallback component rendered when a pattern type has no registered component.
 * Gives homebrew authors feedback instead of silent null.
 */
const UnknownPatternFallback = ({ patternType, mechanicId }) => (
  <div className="p-3 rounded bg-gray-800/50 border border-yellow-600/30 text-sm text-yellow-300">
    <p className="font-medium">Unknown pattern: {patternType}</p>
    <p className="text-gray-400 text-xs mt-1">
      Mechanic "{mechanicId}" requires a pattern component that is not registered.
    </p>
  </div>
);

/**
 * CampaignFrameRenderer - Main orchestrator for campaign frame mechanics UI
 *
 * Routes to the appropriate pattern component via the pattern registry.
 * Supports official SRD patterns and dynamically registered homebrew patterns.
 *
 * @param {string} frameId - The campaign frame identifier (e.g., 'beast-feast', 'witherwild')
 * @param {string} mechanicId - The mechanic identifier (e.g., 'cooking', 'wither-corruption')
 * @param {object} character - Current character data
 * @param {function} updateCharacter - Character update callback
 * @param {object} allData - Game data from DataContext
 * @param {object} sessionState - Session-local state from useFrameSession
 * @param {function} setSessionState - Session state updater
 * @param {function} onClose - Close/dismiss callback
 * @param {string} campaignId - Campaign ID for collaborative features (optional)
 * @param {object} user - Current user for AI features (optional)
 */
const CampaignFrameRenderer = ({
  frameId,
  mechanicId,
  character,
  updateCharacter,
  allData,
  sessionState,
  setSessionState,
  onClose,
  campaignId,
  user,
}) => {
  const config = getFrameMechanicConfig(frameId, mechanicId);

  if (!config) {
    console.warn(`CampaignFrameRenderer: No config for ${frameId}/${mechanicId}`);
    return null;
  }

  const frameStaticData = allData?.campaignFrames?.[frameId] || null;

  const commonProps = {
    frameId,
    mechanicId,
    config,
    character,
    updateCharacter,
    allData,
    frameStaticData,
    sessionState,
    setSessionState,
    onClose,
    campaignId,
    user,
  };

  const PatternComponent = getPatternComponent(config.pattern);

  if (!PatternComponent) {
    console.warn(`CampaignFrameRenderer: Unknown pattern type: ${config.pattern}`);
    return <UnknownPatternFallback patternType={config.pattern} mechanicId={mechanicId} />;
  }

  return <PatternComponent {...commonProps} />;
};

/**
 * Check if a mechanic should render special UI
 */
export const shouldRenderFrameMechanic = (frameId, mechanicId) => {
  return getFrameMechanicConfig(frameId, mechanicId) !== null;
};

/**
 * Get all mechanics for a frame that should be rendered
 */
export const getRenderableMechanics = (frameId) => {
  const { getFrameMechanics } = require('../../config/campaignFrameConfig');
  const mechanics = getFrameMechanics(frameId);
  return Object.keys(mechanics);
};

export default CampaignFrameRenderer;
